import { makeAutoObservable, runInAction } from "mobx";
import { MapColoring, MapColoringType } from "../../map/models/MapColoring";
import {
  addCustomIcon,
  mapActive,
  MapboxStylesUrl,
  mapUtils,
} from "../../map/utils/map-utils";
import { RootStore } from "../../shared/stores/RootStore";
import { PublicCredentials } from "../../auth/models/PublicCredentials";
import { setPasswordAuthAxios } from "../../shared/utils/network-utils";
import { LifeCycleStage } from "../../cra/models/LifeCycleStage";
import { CraWay } from "../../cra/models/CraWay";
import { LayerSpecification, MapMouseEvent } from "mapbox-gl";
import { CustomSource } from "../../map/models/CustomSource";
import { CraMapSource } from "../../map/sources/CraMapSource";
import { HlaMapSource } from "../../map/sources/HlaMapSource";
import { PlannedTrajectoryMapSource } from "../../map/sources/PlannedTrajectoryMapSource";
import { PendingCoordChangesMapSource } from "../../map/sources/PendingCoordChangesMapSource";
import { SurveysMapSource } from "../../map/sources/SurveysMapSource";
import mapboxgl from "mapbox-gl";
import { StreetViewMapSource } from "../../map/sources/StreetViewMapSource";
import { ImportHlaMapSource } from "../../map/sources/import-hla/ImportHlaMapSource";
import { ShiftDragSelection } from "../../map/sources/ShiftDragSelection";
import { MapillaryMapSource } from "../../map/sources/MapillaryMapSource";
import {
  cameraSvg,
  chevronSvg,
  commentSvg,
  simulationSvg,
} from "../../shared/assets";

export enum MapMode {
  HomePage = "HomePage",
  SurveyPage = "SurveyPage",
  ImportHlaAtoB = "ImportHlaAtoB",
  ImportHlaBBox = "ImportHlaBBox",
  ManageNodes = "ManageNodes",
  CraCoding = "CraCoding",
  HlaCoding = "HlaCoding",
  SimulationCoding = "SimulationCoding",
  PlanTrajectory = "PlanTrajectory",
  MapillaryImport = "MapillaryImport",
}

export enum MapPageView {
  Map,
  Dashboard,
}

export enum MapComponent {
  Detail = "detail",
  Filters = "filters",
  Legend = "legend",
  Shortcuts = "shortcuts",
  Comments = "comments",
}

export type MapComponentVisibility = Record<MapComponent, boolean>;

const defaultView = MapPageView.Map;

export class MapStore {
  rootStore;

  map?: mapboxgl.Map;
  mode = MapMode.HomePage;

  craMapSource?: CraMapSource;
  hlaMapSource?: HlaMapSource;
  plannedTrajectoryMapSource?: PlannedTrajectoryMapSource;
  pendingCoordChangesMapSource?: PendingCoordChangesMapSource;
  surveysMapSource?: SurveysMapSource;
  streetViewMapSource?: StreetViewMapSource;
  importHlaMapSource?: ImportHlaMapSource;
  shiftDragSelection?: ShiftDragSelection;
  mapillaryMapSource?: MapillaryMapSource;

  hideUnpublishedContent = false;

  coloring: MapColoring = {
    type: MapColoringType.Score,
    scoreKey: "global",
  };

  view = defaultView;
  showSimulationsCompare = false;
  componentVisibility: MapComponentVisibility = {
    detail: true,
    filters: true,
    legend: true,
    shortcuts: true,
    comments: true,
  };
  selectedStylesUrl = MapboxStylesUrl.Standard;
  mapResizeTrigger = 0;
  mapRecenterTrigger = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.view = defaultView;
      this.showSimulationsCompare = false;
      this.coloring = {
        type: MapColoringType.Score,
        scoreKey: "global",
      };
    });
  }

  setMode(mode: MapMode) {
    runInAction(() => {
      this.mode = mode;
    });

    if (mode !== MapMode.HomePage) {
      const surveyId = this.rootStore.surveyStore.getSelected()?.id;
      if (surveyId) {
        this.rootStore.simulationStore.loadSimulatedHlaWayIds(surveyId);
      }
    }

    this.surveysMapSource?.onModeChange(mode);
    this.hlaMapSource?.onModeChange(mode);
    this.craMapSource?.onModeChange(mode);
    this.importHlaMapSource?.onModeChange(mode);
    this.plannedTrajectoryMapSource?.onModeChange(mode);
    this.shiftDragSelection?.onModeChange(mode);
    this.mapillaryMapSource?.onModeChange(mode);
  }

  isActive() {
    return mapActive(this.map);
  }

  init() {
    const map = new mapboxgl.Map({
      ...mapUtils.defaultConfig,
      preserveDrawingBuffer: true,
    });

    map.on("load", () => {
      map.boxZoom.disable();
      map.addControl(
        new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: "metric",
        }),
      );

      this.addCustomIcons(map);
    });

    map.once("style.load", () => {
      map.resize();
      this.registerMap(map);
      this.initSources();
    });
  }

  initSources() {
    if (!this.map) return;

    this.surveysMapSource?.clear();
    this.surveysMapSource = new SurveysMapSource(this.map, this.rootStore);
    this.hlaMapSource?.clear();
    this.hlaMapSource = new HlaMapSource(this.map, this.rootStore);
    this.craMapSource?.clear();
    this.craMapSource = new CraMapSource(this.map, this.rootStore);
    this.plannedTrajectoryMapSource?.clear();
    this.plannedTrajectoryMapSource = new PlannedTrajectoryMapSource(
      this.map,
      this.rootStore,
    );
    this.pendingCoordChangesMapSource?.clear();
    this.pendingCoordChangesMapSource = new PendingCoordChangesMapSource(
      this.map,
      this.rootStore,
    );
    this.streetViewMapSource?.clear();
    this.streetViewMapSource = new StreetViewMapSource(this.map);
    this.importHlaMapSource?.clear();
    this.importHlaMapSource = new ImportHlaMapSource(this.map, this.rootStore);
    this.shiftDragSelection?.disable();
    this.shiftDragSelection = new ShiftDragSelection(this.map, this.rootStore);
    this.mapillaryMapSource?.clear();
    this.mapillaryMapSource = new MapillaryMapSource(this.map, this.rootStore);

    this.setMode(this.mode);
  }

  onSelectedCraWayChange(craWay?: CraWay) {
    this.shiftDragSelection?.setSelectCraNodes(
      craWay?.lifeCycleStage === LifeCycleStage.DataCollection ||
        craWay?.lifeCycleStage === LifeCycleStage.Coding,
    );
  }

  onSelectedMapillarySequenceChange(sequenceId?: string) {
    this.shiftDragSelection?.setSelectMapillaryPoints(!!sequenceId);
  }

  destroy() {
    this.plannedTrajectoryMapSource?.clear();
    this.plannedTrajectoryMapSource = undefined;

    this.craMapSource?.clear();
    this.craMapSource = undefined;

    this.hlaMapSource?.clear();
    this.hlaMapSource = undefined;

    this.pendingCoordChangesMapSource?.clear();
    this.pendingCoordChangesMapSource = undefined;

    this.surveysMapSource?.clear();
    this.surveysMapSource = undefined;

    this.importHlaMapSource?.disable();
    this.importHlaMapSource = undefined;

    this.mapillaryMapSource?.clear();
    this.mapillaryMapSource = undefined;

    this.map?.remove();
    this.map = undefined;
  }

  setComponentVisibility(
    setter: (prev: MapComponentVisibility) => MapComponentVisibility,
  ) {
    runInAction(() => {
      this.componentVisibility = setter(this.componentVisibility);
    });
  }

  recenterMap() {
    runInAction(() => {
      this.mapRecenterTrigger = this.mapRecenterTrigger + 1;
    });
  }

  centerMap() {
    const { surveyStore } = this.rootStore;
    const selectedSurvey = surveyStore.getSelected();

    if (mapActive(this.map)) {
      if (!selectedSurvey) {
        this.centerHomeMap();
      }
      mapUtils.centerMap(this.map!, (bounds) => {
        this.hlaMapSource?.extendBounds(bounds);
        this.craMapSource?.extendBounds(bounds);
        this.plannedTrajectoryMapSource?.extendBounds(bounds);

        if (bounds.isEmpty() && selectedSurvey) {
          this.map?.flyTo({
            center: [selectedSurvey.city.lng, selectedSurvey.city.lat],
            zoom: 12,
          });
        }
      });
    }
  }

  refreshMapWindow() {
    runInAction(() => {
      this.mapResizeTrigger = this.mapResizeTrigger + 1;
    });
  }

  setView(view: MapPageView) {
    runInAction(() => {
      this.view = view;
    });
  }

  setShowSimulationsCompare(value: boolean) {
    runInAction(() => {
      this.showSimulationsCompare = value;
    });
  }

  setColoring(value: MapColoring) {
    runInAction(() => {
      this.coloring = value;
    });

    const { filterStore } = this.rootStore;
    filterStore.filterNodes();
    filterStore.filterHlaWays();
  }

  setCredentials(credentials: PublicCredentials) {
    runInAction(() => {
      this.hideUnpublishedContent = true;
    });
    setPasswordAuthAxios(credentials);
  }

  registerMap(map: mapboxgl.Map) {
    runInAction(() => {
      this.map = map;
    });
  }

  zoomIn() {
    if (this.isActive()) {
      this.map?.zoomIn();
    }
  }

  zoomOut() {
    if (this.isActive()) {
      this.map?.zoomOut();
    }
  }

  centerHomeMap() {
    if (this.isActive()) {
      this.map?.flyTo({
        center: [5, 43],
        zoom: 3,
        speed: 5,
        pitch: 0,
        bearing: 0,
      });
    }
  }

  setMapStyle(style: MapboxStylesUrl) {
    this.selectedStylesUrl = style;

    const mapStyle = this.map?.getStyle();

    if (!this.map || !mapStyle) return;

    const customSources = Object.values(CustomSource);
    const savedSources = { ...mapStyle.sources };

    Object.keys(savedSources).forEach((sourceName) => {
      if (!customSources.includes(sourceName as CustomSource)) {
        delete savedSources[sourceName];
      }
    });

    // Save the existing sources and layers
    const savedLayers = mapStyle.layers.reduce((list, layer) => {
      if (!customSources.includes(layer.source as CustomSource)) return list;
      return [...list, layer];
    }, [] as LayerSpecification[]);

    // apply the new style, which will delete all existing data
    this.map.setStyle(this.selectedStylesUrl);

    // re-add all the saved sources and layers
    this.map.once("styledata", () => {
      this.addCustomIcons(this.map!);

      // Add back the saved sources
      Object.keys(savedSources).forEach((sourceId) => {
        this.map?.addSource(sourceId, savedSources[sourceId]);
      });

      // Add back the saved layers
      savedLayers.forEach((layer) => {
        this.map?.addLayer(layer);
      });
    });
  }

  listenForClick(callback: (e: MapMouseEvent) => void): () => void {
    var onCancel = () => {};

    if (this.isActive()) {
      const canvas = this.map!.getCanvas();
      canvas.style.cursor = "pointer";

      const callAndCleanup = (e: MapMouseEvent) => {
        callback(e);
        canvas.style.cursor = "";
      };

      this.map?.once("click", callAndCleanup);

      onCancel = () => {
        this.map?.off("click", callAndCleanup);
        canvas.style.cursor = "";
      };
    }

    return onCancel;
  }

  private addCustomIcons(map: mapboxgl.Map) {
    addCustomIcon(map, "camera-icon", cameraSvg, 30);
    addCustomIcon(map, "comment-icon", commentSvg, 24, true);
    addCustomIcon(map, "chevron-icon", chevronSvg, 24, true);
    addCustomIcon(map, "simulation-icon", simulationSvg, 30, true);
  }
}
